<template>
  <div>
    <b-card>
      <b-row>
        <b-col md="6">
          <h1>Gestión de Personas</h1>
        </b-col>
        <b-col md="6" class="text-right">
          <b-button variant="primary" @click="openModal(null)">
            <feather-icon icon="UserPlusIcon" class="mr-50" />
            Nueva Persona
          </b-button>
        </b-col>
      </b-row>

      <!-- Filtros -->
      <b-row class="mt-2">
        <b-col md="6">
          <b-form-group label="Buscar" label-cols="3">
            <b-form-input
              v-model="searchTerm"
              placeholder="Buscar por nombre, apellido o email..."
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Estado" label-cols="3">
            <b-form-select
              v-model="filterStatus"
              :options="filterOptions"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-table
        striped
        hover
        responsive
        class="mt-2"
        :items="filteredPersons"
        :fields="fields"
        :per-page="perPage"
        :current-page="currentPage"
      >
        <template #cell(nombres)="data">
          {{ data.value }}
        </template>
        <template #cell(apellidos)="data">
          {{ data.value }}
        </template>
        <template #cell(email)="data">
          {{ data.value || 'N/A' }}
        </template>
        <template #cell(telefono)="data">
          {{ data.value || 'N/A' }}
        </template>
        <template #cell(estado)="data">
          <b-badge :variant="data.value ? 'light-success' : 'light-danger'">
            {{ data.value ? 'Activo' : 'Inactivo' }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <b-button 
            variant="gradient-primary" 
            class="btn-icon" 
            size="sm"
            @click="openModal(data.item)"
          >
            <feather-icon icon="EditIcon" />
          </b-button>
          <b-button 
            variant="gradient-danger" 
            class="btn-icon ml-1" 
            size="sm"
            @click="deletePerson(data.item)"
          >
            <feather-icon icon="Trash2Icon" />
          </b-button>
        </template>
      </b-table>

      <b-row>
        <b-col cols="12">
          <b-pagination
            v-model="currentPage"
            :total-rows="filteredPersons.length"
            :per-page="perPage"
            align="center"
            size="sm"
            class="my-0"
          />
        </b-col>
      </b-row>
    </b-card>

    <person-modal
      :person-data="selectedPerson"
      @submit="handleModalSubmit"
    />
  </div>
</template>

<script>
import {
  BCard, 
  BRow, 
  BCol, 
  BButton, 
  BTable, 
  BBadge,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BPagination,
} from 'bootstrap-vue'
import PersonModal from '@/components/persons/PersonModal.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import personasService from '@/services/personasService'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BBadge,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BPagination,
    PersonModal,
  },
  data() {
    return {
      fields: [
        { key: 'personaId', label: 'ID', sortable: true },
        { key: 'nombres', label: 'Nombres', sortable: true },
        { key: 'apellidos', label: 'Apellidos', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'telefono', label: 'Teléfono', sortable: true },
        { key: 'estado', label: 'Estado', sortable: true },
        { key: 'actions', label: 'Acciones' },
      ],
      persons: [],
      selectedPerson: null,
      loading: false,
      searchTerm: '',
      filterStatus: 'all',
      filterOptions: [
        { text: 'Todos', value: 'all' },
        { text: 'Activos', value: true },
        { text: 'Inactivos', value: false },
      ],
      currentPage: 1,
      perPage: 10,
    }
  },
  computed: {
    filteredPersons() {
      let filtered = this.persons

      // Filtrar por búsqueda
      if (this.searchTerm) {
        const search = this.searchTerm.toLowerCase()
        filtered = filtered.filter(person => 
          person.nombres.toLowerCase().includes(search) ||
          person.apellidos.toLowerCase().includes(search) ||
          (person.email && person.email.toLowerCase().includes(search))
        )
      }

      // Filtrar por estado
      if (this.filterStatus !== 'all') {
        filtered = filtered.filter(person => person.estado === this.filterStatus)
      }

      return filtered
    },
  },
  async mounted() {
    await this.loadPersons()
  },
  methods: {
    async loadPersons() {
      this.loading = true
      try {
        const response = await personasService.getAll()
        this.persons = response
      } catch (error) {
        console.error('Error al cargar personas:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Error al cargar las personas',
          },
        })
      } finally {
        this.loading = false
      }
    },
    openModal(item) {
      this.selectedPerson = item ? { ...item } : null
      this.$bvModal.show('person-modal')
    },
    async handleModalSubmit(data) {
      try {
        if (data.personaId) {
          // Actualizar persona existente
          await personasService.update(data.personaId, data)
        } else {
          // Crear nueva persona
          await personasService.create(data)
        }
        
        await this.loadPersons()
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Operación Exitosa',
            icon: 'CheckIcon',
            variant: 'success',
            text: `La persona se ha ${data.personaId ? 'actualizado' : 'creado'} correctamente.`,
          },
        })
      } catch (error) {
        console.error('Error en la operación:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Error al procesar la operación',
          },
        })
      }
    },
    async deletePerson(item) {
      this.$swal({
        title: '¿Estás seguro?',
        text: 'Esta persona será desactivada',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, desactivar',
        cancelButtonText: 'Cancelar',
        customClass: { 
          confirmButton: 'btn btn-primary', 
          cancelButton: 'btn btn-outline-danger ml-1' 
        },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) {
          try {
            await personasService.delete(item.personaId)
            await this.loadPersons()
            
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Desactivado',
                icon: 'TrashIcon',
                variant: 'success',
                text: 'La persona ha sido desactivada.',
              },
            })
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: 'Error al desactivar la persona',
              },
            })
          }
        }
      })
    },
  },
}
</script>

<style scoped>
.btn-icon {
  padding: 0.5rem;
}
</style>

